import { getIntl } from "client/utils/internationalization";

import { Field } from "../../field";
import { FieldSet } from "../../fieldset";
import { Merchant } from "../../merchant";
import {
  CountryList,
  I18nValues,
  MerchantField,
  TotalAnnualSales,
} from "../../types";

type CountryListTotalAnnualSalesMap = {
  [key in CountryList]: TotalAnnualSales[];
};

/**
 * AU / NZ & US / CA are the same. Consider extracting? Premature optimization?
 * What happens if they deviate? Obv we could spread common, but is
 * this desirable?
 */
const countryListTotalAnnualSalesMap: CountryListTotalAnnualSalesMap = {
  Australia: [
    "$0-$100K",
    "$100-$500K",
    "$500k-$1m",
    "$1 - $2M",
    "$2 - $3M",
    "$3-$5M",
    "$5-$10M",
    "$10-$25M",
    "$25-$55M",
    "$55M+",
  ],
  Canada: [
    "Under $100,000",
    "Between $100,000 and $500,000",
    "Between $500,000 and $1 Million",
    "Between $1 Million and $2 Million",
    "Between $2 Million and $5 Million",
    "Between $5 Million and $10 Million",
    "Between $10 Million and $25 Million",
    "Between $25 Million and $80 Million",
    "Between $80 Million and $200 Million",
    "Between $200 Million and $300 Million",
    "Over $301 Million",
  ],
  "New Zealand": [
    "$0-$100K",
    "$100-$500K",
    "$500k-$1m",
    "$1 - $2M",
    "$2 - $3M",
    "$3-$5M",
    "$5-$10M",
    "$10-$25M",
    "$25-$55M",
    "$55M+",
  ],
  "United Kingdom": [
    "Up to £100k",
    "£100k - 500k",
    "£500k - £1m",
    "£1m to £5m",
    "£5m to £10m",
    "£10m to £30m",
    "£30m to £80m",
    "£80m+",
  ],
  "United States": [
    "Under $100,000",
    "Between $100,000 and $500,000",
    "Between $500,000 and $1 Million",
    "Between $1 Million and $2 Million",
    "Between $2 Million and $5 Million",
    "Between $5 Million and $10 Million",
    "Between $10 Million and $25 Million",
    "Between $25 Million and $80 Million",
    "Between $80 Million and $200 Million",
    "Between $200 Million and $300 Million",
    "Over $301 Million",
  ],
};

export class TotalAnnualSalesField extends Field<TotalAnnualSales> {
  constructor(
    model: Merchant,
    fieldSet: FieldSet<any>,
    opts: Partial<MerchantField<TotalAnnualSales>> = {}
  ) {
    super(
      model,
      {
        dataType: "string",
        propertyName: "totalAnnualSales",
        uiFieldType: "Select",
        ...opts,
      },
      fieldSet
    );
  }

  get i18nValues(): I18nValues {
    const country = this._model.companyInformation.registeredCountry.value;
    const channel = this._model.lead.salesChannel.value;

    const t = getIntl(country);

    if (channel === "Online and In-store") {
      return {
        label: t("totalAnnualSalesOverall"),
      };
    }

    return {
      label: t(this.propertyName),
    };
  }

  get testValue(): any {
    const registeredCountry = this._model.companyInformation.registeredCountry
      .value;

    return registeredCountry
      ? countryListTotalAnnualSalesMap[registeredCountry][0]
      : "Under $100,000";
  }

  get pickList(): TotalAnnualSales[] {
    const registeredCountry = this._model.companyInformation.registeredCountry
      .value;

    return registeredCountry
      ? countryListTotalAnnualSalesMap[registeredCountry]
      : [];
  }

  get applicable(): boolean {
    return (
      typeof this._model.companyInformation.registeredCountry.value !==
      "undefined"
    );
  }

  get required(): boolean {
    return this.applicable;
  }

  get shown(): boolean {
    return this.applicable;
  }
}
